'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/ml-extra'

const ScalingDescent = dynamic(() => import('../_components/widgets/MLExtraWidgets').then(m => m.ScalingDescent), { ssr: false })

export default function MultipleFeatures() {
  return (
    <Lesson>
      <Step>
        <H>More than one clue</H>
        <P>
          A house’s price doesn’t depend on its size alone. Bedrooms, age and distance to the city centre all matter. With
          several <Hl c="blue">features</Hl>, the line becomes a weighted sum: one <Hl c="green">weight</Hl> per feature, plus
          a bias. You’ve seen this shape before. It’s a dot product.
        </P>
        <MB>{'\\amber{\\hat{y}} = \\green{w_1}\\blue{x_1} + \\green{w_2}\\blue{x_2} + \\dots + \\green{w_d}\\blue{x_d} + \\green{b} = \\green{\\mathbf{w}} \\cdot \\blue{\\mathbf{x}} + \\green{b}'}</MB>
        <P>
          Training works exactly as before. Each weight gets its own partial derivative, and they all step downhill together.
          Let’s try it on 200 houses. Note the scales: sizes in the hundreds, bedrooms between 1 and 6.
        </P>
        <Exercise id="raw-features" title="Training on raw features" {...EX.rawFeatures} />
        <P>
          A small learning rate barely moves. A slightly bigger one blows up. There’s no good setting. Why?
        </P>
      </Step>

      <Step gate="q-scale">
        <H>Stretched valleys</H>
        <P>
          With one weight per feature, the loss is a landscape over all the weights. When features have very different scales,
          that landscape becomes a long, thin valley. The gradient points mostly across the valley rather than along it, so
          gradient descent zigzags and crawls.
        </P>
        <Figure n="6.2" wide caption="Each oval is a contour of equal loss over two weights. Raw features: a squashed valley and a slow, zigzagging path. Standardised: a round bowl, and a straight dash to the bottom.">
          <ScalingDescent />
        </Figure>
        <Quiz
          id="q-scale"
          question="Why does standardising features (mean 0, spread 1) make gradient descent faster?"
          options={[
            { label: 'It makes the loss bowl round, so the steepest direction points straight at the bottom', correct: true },
            { label: 'It removes noise from the data', why: 'Standardising only shifts and rescales each feature. The noise is still there.' },
            { label: 'It reduces the number of features', why: 'Same number of features, just measured in comparable units.' },
          ]}
          explain="That’s why the tensors lesson had you write standardize(). Nearly every real ML pipeline does it first."
        />
      </Step>

      <Step gate="multi-fit">
        <H>Fit all four at once</H>
        <P>
          Now standardise first, then train. Afterwards, divide each weight by its feature’s spread to turn it back into
          everyday units: thousands per square metre, per bedroom, per year of age.
        </P>
        <Exercise
          id="multi-fit"
          title="predict and train"
          {...EX.multiFit}
          hints={[
            <>
              <C>{'return vec.dot(w, x) + b'}</C>
            </>,
            <>
              Inside the epoch loop: for each example, <C>{'const err = predict(w, b, Z[i]) - y[i]'}</C>; add{' '}
              <C>{'2 * err * Z[i][j] / n'}</C> to each <C>gw[j]</C> and <C>{'2 * err / n'}</C> to <C>gb</C>. Then step.
            </>,
          ]}
        />
        <Callout kind="plain">
          The weights are explanations. “Each extra square metre adds about 2 thousand” is the model telling you what it
          learned. Interpretable weights are one of the great strengths of linear models — and something you lose with big
          neural networks.
        </Callout>
        <Callout kind="warn">
          Careful reading weights when features move together. Bigger houses have more bedrooms, so the bedroom weight only
          means “extra value of a bedroom <em>at the same size</em>”. When features overlap a lot, individual weights can get
          strange even when predictions are fine.
        </Callout>
      </Step>
    </Lesson>
  )
}
